import { useCallback, useEffect, useMemo } from "react"

import { useTranslation } from "react-i18next"

import { useContentSidebarStore } from "@/stores/content-sidebar-store"

import { AITranslatorSettingsSidebar } from "../components/ai-translator-settings-sidebar"

export const TRANSLATE_SETTINGS_PANEL_ID = "translate-settings"

export function useTranslateSettingsPanel(glossaryOnly = false) {
  const { t } = useTranslation()
  const openContentPanel = useContentSidebarStore((s) => s.openPanel)
  const closeContentPanel = useContentSidebarStore((s) => s.closePanel)
  const currentContentPanelId = useContentSidebarStore((s) => s.currentPanel?.id)
  const isSettingsPanelOpen = currentContentPanelId === TRANSLATE_SETTINGS_PANEL_ID

  const openSettingsPanel = useCallback(() => {
    openContentPanel({
      id: TRANSLATE_SETTINGS_PANEL_ID,
      title: t("pages.common.settings"),
      content: <AITranslatorSettingsSidebar glossaryOnly={glossaryOnly} />,
      focusable: true,
      closeable: true,
      resizable: true,
    })
  }, [openContentPanel, t, glossaryOnly])

  const settingsSidebarTriggerLabel = useMemo(() => {
    if (isSettingsPanelOpen) {
      return t("analysispanel.close_sidebar", { defaultValue: "Close sidebar" })
    }
    return t("pages.common.settings")
  }, [isSettingsPanelOpen, t])

  const toggleSettingsPanel = useCallback(() => {
    if (isSettingsPanelOpen) {
      closeContentPanel()
      return
    }
    openSettingsPanel()
  }, [closeContentPanel, isSettingsPanelOpen, openSettingsPanel])

  useEffect(() => {
    if (isSettingsPanelOpen) {
      openSettingsPanel()
    }
  }, [isSettingsPanelOpen, openSettingsPanel])

  useEffect(() => {
    return () => {
      const state = useContentSidebarStore.getState()
      if (state.currentPanel?.id === TRANSLATE_SETTINGS_PANEL_ID) {
        state.forceClosePanel()
      }
    }
  }, [])

  return { isSettingsPanelOpen, settingsSidebarTriggerLabel, toggleSettingsPanel }
}
